import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { useExportSeeds } from '@/hooks/use-seeds';
import type { PracticeSeedListParams, ImportFormat } from '@/types/seeds';

export interface ExportSeedsMenuProps {
  filterParams: PracticeSeedListParams;
  selectedIds: string[];
}

export function ExportSeedsMenu({ filterParams, selectedIds }: ExportSeedsMenuProps) {
  const mutation = useExportSeeds();

  const handleExport = async (format: ImportFormat, onlySelected: boolean) => {
    try {
      const { blob, filename } = await mutation.mutateAsync({
        format,
        filter_params: onlySelected ? undefined : filterParams,
        selected_ids: onlySelected ? selectedIds : undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(`Export failed: ${(e as Error).message}`);
    }
  };

  const hasSelection = selectedIds.length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={(p) => (
          <Button {...p} variant="outline" size="sm">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        )}
      />
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuGroup>
          <DropdownMenuLabel>All matching filters</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => handleExport('csv', false)}>
            Export as CSV
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleExport('xlsx', false)}>
            Export as XLSX
          </DropdownMenuItem>
        </DropdownMenuGroup>
        {hasSelection && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel>Selected ({selectedIds.length})</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleExport('csv', true)}>
                Export selected as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('xlsx', true)}>
                Export selected as XLSX
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
